<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { RoomMessage } from '../interfaces/Room.interface';
import { useRoute } from 'vue-router';
import { push, set, ref as dbRef } from 'firebase/database';
import { db } from '../configs/firebase';
import { useState } from '../composables/useState';
import { useTranslations } from '../composables/useTranslations';

const props = defineProps<{
  messages: Record<string, RoomMessage>;
}>();

const route = useRoute();
const { username } = useState();
const t = useTranslations();

const chatMessage = ref('');
const messageInput = ref<HTMLTextAreaElement | null>(null);
const chatMessages = ref<HTMLDivElement | null>(null);
const autoResize = () => {
  if (!messageInput.value) return;
  messageInput.value.style.height = 'auto';
  messageInput.value.style.height = `${Math.min(150, messageInput.value.scrollHeight)}px`;
};

const scrollChat = () => {
  if (chatMessages.value) {
    chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
  }
};

async function addMessage() {
  if (!chatMessage.value) {
    console.info('Message is required');
    return;
  }

  const roomId = route.params['roomId'] as string;

  if (!roomId) {
    console.error('Room ID is required');
    return;
  }

  const roomRef = dbRef(db, `rooms/${roomId}/messages`);
  const newMessageRef = push(roomRef);
  const newMessage: RoomMessage = {
    id: newMessageRef.key ?? '',
    createdAt: new Date().toISOString(),
    createdBy: username.value ?? 'Anonymous',
    message: chatMessage.value,
  };
  await set(newMessageRef, newMessage);
  chatMessage.value = '';

  // Reset textarea height after sending
  if (messageInput.value) {
    messageInput.value.style.height = '38px';
  }

  scrollChat();
}

onMounted(() => {
  scrollChat();
});

watch(
  () => Object.keys(props.messages).length,
  async () => {
    await nextTick();
    scrollChat();
  },
);
</script>

<template>
  <!-- Right Sidebar: Chat -->
  <div
    class="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col flex-grow min-h-0"
  >
    <div class="p-4 border-b border-gray-100 flex items-center justify-between">
      <h2 class="font-bold text-gray-800 flex items-center text-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="mr-2"
        >
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
          ></path>
        </svg>
        Chat
      </h2>
    </div>

    <!-- Chat Messages -->
    <div
      ref="chatMessages"
      class="flex-grow overflow-y-auto min-h-0 p-4 space-y-4"
    >
      <div v-for="message in Object.values(messages)" :key="message.id">
        <div class="flex justify-between items-center">
          <div class="text-base font-medium">{{ message.createdBy }}</div>
          <div
            class="text-xs text-gray-500"
            :title="new Date(message.createdAt).toLocaleString()"
          >
            {{
              new Date(message.createdAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            }}
          </div>
        </div>
        <div class="text-sm whitespace-pre-wrap">{{ message.message }}</div>
      </div>

      <div v-if="Object.keys(messages).length === 0">
        <div class="text-center text-gray-500">
          {{ t.noMessagesYetStartTheConversation }}
        </div>
      </div>
    </div>

    <!-- Chat Input -->
    <div class="p-3 border-t border-gray-100 flex-shrink-0">
      <form class="flex items-center gap-2" @submit.prevent="addMessage">
        <textarea
          ref="messageInput"
          v-model="chatMessage"
          :placeholder="t.typeAMessage"
          class="flex-grow py-2 px-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dd7631]/50 focus:border-[#dd7631]"
          rows="1"
          style="
            min-height: 38px;
            max-height: 150px;
            overflow-y: auto;
            resize: none;
          "
          @input="autoResize"
          @keydown.enter="
            $event.shiftKey ? null : ($event.preventDefault(), addMessage())
          "
        ></textarea>
        <button
          type="submit"
          class="p-2 bg-[#dd7631] text-white rounded-lg hover:bg-[#c56a2c] transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </form>
    </div>
  </div>
</template>
